/* Verdo cart glue (drawer + cart page). Dawn's cart.js / cart-drawer.js do the work; this keeps the pieces in step:
   - the "Your cart (n)" count after a batched removal (cart.js only repaints the drawer footer then)
   - on /cart, the drawer and the page refresh each other (cart.js ignores its own "cart-items" events, so a change
     made in one left the other stale, and a batched removal on the page never updated the page totals)
   - the drawer's "Remove all items" button, with a second press to confirm
   - the drawer's "You may also like" Add buttons (no quick add modal: the drawer sits above it)
   - the phone "Subtotal + Check out" bar on /cart. */
(() => {
  if (window.__vcCart) return;
  window.__vcCart = true;

  const drawerEl = () => document.querySelector('cart-drawer');
  const pageItems = () => document.getElementById('main-cart-items');
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function setCounts(count) {
    document.querySelectorAll('[data-vc-cart-count]').forEach((el) => { el.textContent = count; });
    document.querySelectorAll('[data-vc-cart-count-label]').forEach((el) => { el.textContent = `${count} ${count === 1 ? 'item' : 'items'}`; });
  }

  /* Repaint the drawer from a fresh render without opening or closing it. */
  function paintDrawer(html, { focus = false } = {}) {
    const drawer = drawerEl();
    const target = document.getElementById('CartDrawer');
    if (!drawer || !target || !html) return;
    const doc = parse(html);
    const fresh = doc.querySelector('#CartDrawer');
    if (!fresh) return;
    const freshRoot = doc.querySelector('cart-drawer');
    const empty = freshRoot ? freshRoot.classList.contains('is-empty') : false;
    target.innerHTML = fresh.innerHTML;
    drawer.classList.toggle('is-empty', empty);
    const overlay = target.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', () => drawer.close());
    if (focus && drawer.classList.contains('active') && typeof trapFocus === 'function') {
      if (empty) {
        const box = drawer.querySelector('.drawer__inner-empty');
        if (box) trapFocus(box, box.querySelector('a[href]') || box.querySelector('button'));
      } else {
        trapFocus(target, drawer.querySelector('.drawer__inner'));
      }
    }
  }

  function paintBubble(html) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    const s = parse(html).querySelector('.shopify-section');
    if (s) bubble.innerHTML = s.innerHTML;
  }

  /* Repaint the /cart sections, keeping keyboard focus on the same control of the same line. */
  function paintPage(json, itemsId, footerId) {
    const itemsEl = pageItems();
    const cartItems = document.querySelector('cart-items');
    const footer = document.getElementById('main-cart-footer');
    let empty = null;
    if (itemsEl && json[itemsId]) {
      const doc = parse(json[itemsId]);
      const fresh = doc.querySelector('#main-cart-items .js-contents');
      const target = itemsEl.querySelector('.js-contents');
      const active = document.activeElement;
      let refocus = null;
      if (active && itemsEl.contains(active)) {
        const row = active.closest('.cart-item');
        refocus = row ? { row: row.id, name: active.getAttribute('name'), remove: active.classList.contains('vc-cart-line__remove') } : null;
      }
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      const freshRoot = doc.querySelector('cart-items');
      if (freshRoot) empty = freshRoot.classList.contains('is-empty');
      if (refocus) {
        const row = document.getElementById(refocus.row);
        const el = row && (refocus.remove ? row.querySelector('.vc-cart-line__remove') : row.querySelector(`[name="${refocus.name}"]`));
        if (el) el.focus({ preventScroll: true });
      }
    }
    if (footer && json[footerId]) {
      const doc = parse(json[footerId]);
      const fresh = doc.querySelector('#main-cart-footer .js-contents');
      const target = footer.querySelector('.js-contents');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      const freshRoot = doc.querySelector('#main-cart-footer');
      if (freshRoot && empty === null) empty = freshRoot.classList.contains('is-empty');
    }
    if (empty !== null) {
      if (cartItems) cartItems.classList.toggle('is-empty', empty);
      if (footer) footer.classList.toggle('is-empty', empty);
      document.querySelectorAll('#checkout, .vc-cart-sticky__btn').forEach((btn) => { btn.disabled = empty; });
    }
  }

  let timer = null;
  let pending = { page: false, drawer: false, focusDrawer: false };
  function schedule(parts) {
    pending = {
      page: pending.page || !!parts.page,
      drawer: pending.drawer || !!parts.drawer,
      focusDrawer: pending.focusDrawer || !!parts.focusDrawer,
    };
    clearTimeout(timer);
    timer = setTimeout(refresh, 180);
  }

  async function refresh() {
    const want = pending;
    pending = { page: false, drawer: false, focusDrawer: false };
    const itemsEl = pageItems();
    const footer = document.getElementById('main-cart-footer');
    const itemsId = itemsEl && itemsEl.dataset.id;
    const footerId = footer && footer.dataset.id;
    const ids = [];
    if (want.page && itemsId) ids.push(itemsId);
    if (want.page && footerId) ids.push(footerId);
    if (want.drawer && drawerEl()) ids.push('cart-drawer');
    if (!ids.length) return;
    try {
      // No Accept: application/json here: /cart answers that with cart JSON instead of the rendered sections.
      const res = await fetch(`${window.routes.cart_url}?sections=${ids.join(',')}`);
      if (!res.ok) throw new Error(`Cart refresh failed: ${res.status}`);
      const json = await res.json();
      if (want.page) paintPage(json, itemsId, footerId);
      if (want.drawer) paintDrawer(json['cart-drawer'], { focus: want.focusDrawer });
    } catch (e) {
      console.error('[vc-cart]', e);
    }
  }

  /* Remember the last control used inside the drawer (by line id + name, since lines are re-rendered). */
  let lastDrawerControl = null;
  document.addEventListener('focusin', (e) => {
    const box = document.getElementById('CartDrawer');
    if (!box || !box.contains(e.target)) return;
    const row = e.target.closest('.cart-item');
    lastDrawerControl = { row: row ? row.id : null, name: e.target.getAttribute('name'), remove: e.target.classList.contains('vc-cart-line__remove') };
  });

  /* On /cart, cart.js focuses the page's line (CartItem-N) after a change made in the open drawer, which takes
     focus out of the dialog (Escape and the focus trap stop working). Put it back where it was. */
  function keepFocusInDrawer() {
    const drawer = drawerEl();
    const box = document.getElementById('CartDrawer');
    if (!drawer || !box || !drawer.classList.contains('active') || box.contains(document.activeElement)) return;
    let el = null;
    if (lastDrawerControl && lastDrawerControl.row) {
      const row = document.getElementById(lastDrawerControl.row);
      if (row) el = lastDrawerControl.remove ? row.querySelector('.vc-cart-line__remove') : row.querySelector(`[name="${lastDrawerControl.name}"]`);
    }
    el = el || drawer.querySelector('.drawer__inner');
    if (typeof trapFocus === 'function') trapFocus(box, el);
    else if (el) el.focus();
  }

  function onCartUpdate(event) {
    const data = event && event.cartData;
    if (data && typeof data.item_count === 'number') setCounts(data.item_count);
    const drawer = drawerEl();
    const drawerOpen = !!(drawer && drawer.classList.contains('active'));
    const repainted = !!(data && data.sections); // cart.js already repainted the part that changed
    if (drawerOpen) keepFocusInDrawer();
    if (pageItems()) {
      if (event.source === 'product-form') { schedule({ page: true }); return; }
      if (drawerOpen) { schedule({ page: true, drawer: !repainted, focusDrawer: !repainted }); return; }
      schedule({ drawer: true, page: !repainted });
      return;
    }
    // A batched removal in the drawer only repainted the footer: refresh lines, count and button.
    if (drawer && event.source === 'cart-items' && !repainted) schedule({ drawer: true, focusDrawer: true });
  }

  function boot() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Enter in a quantity box applies the typed quantity. Without this the browser submits the cart form, and its
     default button is the checkout button (form="CartDrawer-Form" / form="cart"), so the shopper was sent to
     checkout instead of seeing the new quantity. cart.js listens for "change" and updates the line. */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || e.isComposing) return;
    const input = e.target && e.target.closest ? e.target.closest('.quantity__input') : null;
    if (!input || !input.closest('cart-drawer-items, cart-items')) return;
    e.preventDefault();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* An emptied (or non-numeric) quantity box goes back to its last quantity. cart.js would otherwise run its step
     check on NaN and pop up "You can only add this item in increments of 1". Runs in the capture phase, before
     cart.js sees the change. To remove a line the shopper can type 0, use minus at 1, or press Remove. */
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input || !input.classList || !input.classList.contains('quantity__input')) return;
    if (!input.closest('cart-drawer-items, cart-items')) return;
    if (input.value.trim() !== '' && !Number.isNaN(parseInt(input.value, 10))) return;
    e.stopImmediatePropagation();
    input.value = input.getAttribute('value') || '1';
  }, true);

  /* Asking for more than Shopify will sell: /cart/change answers 422 {"message": "Only 15 items were added to your
     cart due to availability."} but still changes the cart, and cart.js expects a cart in the reply. The line ended
     up at the new quantity with the header bubble and the "(n items)" count stale and no word about why. Watch the
     change requests (the promise handed back is the original one), then repaint everything from the server and
     say so on the line. Shopify's own wording is used unless it carries a number (stock counts here are not real). */
  const REFUSED_MSG = "We can't send that many right now, so we have set the most we can.";
  async function afterRefusedChange(request, serverMessage) {
    await new Promise((r) => setTimeout(r, 450)); // let cart.js finish its own handling first
    const itemsEl = pageItems();
    const footer = document.getElementById('main-cart-footer');
    const ids = ['cart-icon-bubble'];
    if (drawerEl()) ids.push('cart-drawer');
    if (itemsEl && itemsEl.dataset.id) ids.push(itemsEl.dataset.id);
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    try {
      const res = await fetch(`${window.routes.cart_url}?sections=${ids.join(',')}`);
      if (!res.ok) throw new Error(`Cart refresh failed: ${res.status}`);
      const json = await res.json();
      const drawer = drawerEl();
      const drawerOpen = !!(drawer && drawer.classList.contains('active'));
      if (itemsEl) paintPage(json, itemsEl.dataset.id, footer && footer.dataset.id);
      if (drawer) paintDrawer(json['cart-drawer']);
      paintBubble(json['cart-icon-bubble']);
      const fresh = document.querySelector('[data-vc-cart-count]');
      if (fresh) setCounts(parseInt(fresh.textContent, 10) || 0);
      ['cart-errors', 'CartDrawer-CartErrors'].forEach((id) => { const el = document.getElementById(id); if (el) el.textContent = ''; });
      const message = serverMessage && !/\d/.test(serverMessage) ? serverMessage : REFUSED_MSG;
      const rows = [];
      if (request.id) document.querySelectorAll(`.vc-cart-line__remove[data-variant-id="${request.id}"]`).forEach((b) => rows.push(b.closest('.cart-item')));
      else if (request.line) ['CartItem-', 'CartDrawer-Item-'].forEach((p) => rows.push(document.getElementById(p + request.line)));
      let focusTarget = null;
      rows.filter(Boolean).forEach((row) => {
        const text = row.querySelector('.cart-item__error-text');
        if (text) text.textContent = message;
        if (drawerOpen ? row.closest('#CartDrawer') : !row.closest('#CartDrawer')) focusTarget = row.querySelector('.quantity__input');
      });
      if (drawerOpen && typeof trapFocus === 'function') trapFocus(document.getElementById('CartDrawer'), focusTarget || drawer.querySelector('.drawer__inner'));
      else if (focusTarget) focusTarget.focus({ preventScroll: true });
    } catch (e) {
      console.error('[vc-cart]', e);
    }
  }
  if (typeof window.fetch === 'function' && !window.fetch.__vcCart) {
    const nativeFetch = window.fetch;
    const watched = function (input, init) {
      const promise = nativeFetch.apply(window, arguments);
      let url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { url = ''; }
      if (/\/cart\/change(\.js)?(\?|$)/.test(url)) {
        promise.then((res) => {
          if (res.status !== 422) return;
          let request = {};
          try { request = JSON.parse((init && init.body) || '{}'); } catch (e) { request = {}; }
          let body = Promise.resolve({});
          try { body = res.clone().json().catch(() => ({})); } catch (e) { /* body already read: use our own wording */ }
          body.then((b) => afterRefusedChange(request, b && (b.message || b.description)));
        }).catch(() => {});
      }
      return promise;
    };
    watched.__vcCart = true;
    window.fetch = watched;
  }

  /* Clicking outside closes the drawer even after a repaint dropped the overlay's own listener
     (cart.js replaces #CartDrawer when a removal empties the cart and does not re-bind it). */
  document.addEventListener('click', (e) => {
    if (!e.target || e.target.id !== 'CartDrawer-Overlay') return;
    const drawer = drawerEl();
    if (drawer && drawer.classList.contains('active')) drawer.close();
  });

  /* "Remove all items": first press arms it, second press within 4s clears the cart. */
  const CLEAR_LABEL = 'Remove all items';
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-vc-clear-cart]');
    if (!btn) return;
    e.preventDefault();
    if (btn.dataset.armed !== 'true') {
      btn.dataset.armed = 'true';
      btn.textContent = 'Select again to remove all';
      clearTimeout(btn._vcTimer);
      btn._vcTimer = setTimeout(() => { btn.dataset.armed = ''; btn.textContent = CLEAR_LABEL; }, 4000);
      return;
    }
    clearTimeout(btn._vcTimer);
    btn.disabled = true;
    btn.textContent = 'Removing...';
    try {
      const r = await fetch(`${window.routes.cart_url}/clear.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });
      if (!r.ok) throw new Error(`Clear failed: ${r.status}`);
      const cart = await r.json();
      const res = await fetch(`${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
      const json = await res.json();
      paintDrawer(json['cart-drawer'], { focus: true });
      paintBubble(json['cart-icon-bubble']);
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vc-clear', cartData: cart });
    } catch (err) {
      console.error('[vc-cart]', err);
      btn.disabled = false;
      btn.dataset.armed = '';
      btn.textContent = CLEAR_LABEL;
    }
  });

  /* "You may also like" in the drawer: Add puts the product's first available variant in the cart through
     /cart/add.js and repaints the drawer and the header bubble from the same response. The new line is focused
     (it sits at the top of the list) and announced. Shopify's wording is shown on failure unless it quotes a number
     (stock counts here are not real). */
  const ADD_ERROR = "We couldn't add that to your cart. Please try again.";
  document.addEventListener('click', async (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-vc-rec-add]') : null;
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-disabled') === 'true') return;
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;
    const quantity = Math.max(1, parseInt(btn.dataset.qty, 10) || 1);
    const title = btn.dataset.title || '';
    const errors = document.getElementById('CartDrawer-CartErrors');
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('is-loading');
    if (errors) errors.textContent = '';
    try {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }], sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const msg = data && (data.description || data.message);
        const err = new Error(msg && typeof msg === 'string' && !/\d/.test(msg) ? msg : ADD_ERROR);
        err.shopper = true;
        throw err;
      }
      const sections = data.sections || {};
      if (sections['cart-drawer']) paintDrawer(sections['cart-drawer']);
      else schedule({ drawer: true, focusDrawer: true });
      paintBubble(sections['cart-icon-bubble']);
      const countEl = document.querySelector('#CartDrawer [data-vc-cart-count]');
      const count = countEl ? parseInt(countEl.textContent, 10) : NaN;
      if (!Number.isNaN(count)) setCounts(count);
      // Keyboard users land on the new line (top of the list); after a tap focus stays on the dialog itself, so no
      // focus ring is drawn around a title the shopper did not select. Either way the status line announces it.
      const box = document.getElementById('CartDrawer');
      const removeBtn = box && box.querySelector(`.vc-cart-line__remove[data-variant-id="${variantId}"]`);
      const line = removeBtn && removeBtn.closest('.cart-item');
      const viaKeyboard = e.detail === 0;
      const focusEl = (viaKeyboard && line && line.querySelector('.cart-item__name')) || (box && box.querySelector('.drawer__inner'));
      if (box && focusEl) {
        if (typeof trapFocus === 'function') trapFocus(box, focusEl);
        else focusEl.focus();
      }
      const live = document.getElementById('CartDrawer-LiveRegionText');
      if (live) setTimeout(() => { live.textContent = title ? `${title} added to your cart.` : 'Added to your cart.'; }, 120);
      // source 'cart-items': Dawn's cart-items elements skip it (the drawer is already fresh); on /cart this file
      // refreshes the page sections.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const cartData = Object.assign({}, data);
        if (!Number.isNaN(count)) cartData.item_count = count;
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData });
      }
    } catch (err) {
      if (!err.shopper) console.error('[vc-cart]', err);
      if (errors) errors.textContent = err.shopper ? err.message : ADD_ERROR;
      if (document.contains(btn)) {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('is-loading');
      }
    }
  });

  /* Arrows beside "You may also like" (mouse users): one card per press; each arrow is disabled at its end. */
  function syncRecsNav(track) {
    const recs = track && track.closest('.vc-drawer-recs');
    if (!recs) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    recs.querySelectorAll('[data-vc-recs-step]').forEach((b) => {
      b.disabled = b.dataset.vcRecsStep === '-1' ? track.scrollLeft <= 2 : track.scrollLeft >= max;
    });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-vc-recs-step]') : null;
    if (!btn) return;
    const track = btn.closest('.vc-drawer-recs') && btn.closest('.vc-drawer-recs').querySelector('.vc-drawer-recs__track');
    const card = track && track.querySelector('.vc-drawer-rec');
    if (!card) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: Number(btn.dataset.vcRecsStep) * (card.getBoundingClientRect().width + 10), behavior: reduce ? 'auto' : 'smooth' });
  });
  document.addEventListener('scroll', (e) => {
    if (e.target && e.target.classList && e.target.classList.contains('vc-drawer-recs__track')) syncRecsNav(e.target);
  }, true);

  /* /cart on phones: a "Subtotal + Check out" bar while the summary's own Check out button is still further down.
     It goes away once that button is on screen or scrolled past (so it never sits over the summary, the
     suggestions or the footer) and copies the summary's total whenever cart.js or this file repaints it. */
  function initStickyCheckout() {
    const bar = document.querySelector('[data-vc-cart-sticky]');
    const mainBtn = document.getElementById('checkout');
    const footer = document.getElementById('main-cart-footer');
    if (!bar || !mainBtn || !footer || !('IntersectionObserver' in window)) return;
    const value = bar.querySelector('[data-vc-cart-sticky-total]');
    const summary = footer.querySelector('.vc-cart-summary') || footer;
    const sync = () => {
      const src = summary.querySelector('.totals__total-value');
      if (src && value && value.textContent !== src.textContent) value.textContent = src.textContent;
    };
    new MutationObserver(sync).observe(summary, { subtree: true, childList: true, characterData: true });
    new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const bottom = entry.rootBounds ? entry.rootBounds.bottom : window.innerHeight;
      bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top >= bottom);
    }).observe(mainBtn);
    bar.hidden = false;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initStickyCheckout);
  else initStickyCheckout();
})();
